import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoginMutation, useRegisterMutation, useForgotPasswordMutation, useResetPasswordMutation, useGetCurrentUserQuery, AuthUserResponse, AuthLoginResponse, Role } from '../api/authApi';
import { getRoleName } from '../utils/roleUtils';
import { useLoading } from './LoadingContext';

interface User {
  id: string;
  email: string;
  name: string;
  role: string; // Always store as string for consistency
}

// Helper function to transform AuthUserResponse to User
const transformUser = (authUser: AuthUserResponse): User => ({
  id: authUser.id,
  email: authUser.email,
  name: authUser.name || `${authUser.firstName} ${authUser.lastName}`,
  role: getRoleName(authUser.role), // Extract role name safely
});

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  isPublicRoute: boolean;
  login: (email: string, password: string) => Promise<AuthLoginResponse>;
  logout: () => Promise<void>;
  register: (userData: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { showLoader, hideLoader, updateProgress, updateMessage } = useLoading();
  const [loginMutation] = useLoginMutation();
  const [registerMutation] = useRegisterMutation();
  const token = localStorage.getItem('token');
  const { data: currentUser, error: currentUserError } = useGetCurrentUserQuery(undefined, {
    skip: !token,
  });
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPublicRoute, setIsPublicRoute] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        // Ensure the parsed user matches our User interface
        if (typeof parsedUser === 'object' && parsedUser !== null && 
            typeof parsedUser.id === 'string' && 
            typeof parsedUser.email === 'string' && 
            typeof parsedUser.name === 'string' && 
            (typeof parsedUser.role === 'string' || (typeof parsedUser.role === 'object' && parsedUser.role?.name))) {
          
          // Handle both string and object role formats
          const normalizedUser = {
            ...parsedUser,
            role: typeof parsedUser.role === 'string' ? parsedUser.role : parsedUser.role.name
          };
          
          setUser(normalizedUser);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } else {
      // Clear user state if no token exists
      setUser(null);
    }
    setLoading(false);
  }, []);

  // Update user state when current user data changes
  useEffect(() => {
    if (currentUser) {
      const transformedUser = transformUser(currentUser);
      setUser(transformedUser);
      localStorage.setItem('user', JSON.stringify(transformedUser));
    } else if (currentUserError) {
      // If there's an error fetching current user, clear the token and user
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  }, [currentUser, currentUserError]);

  const checkPublicRoute = () => {
    const currentPath = window.location.pathname;
    const publicRoutes = ['/', '/about', '/services', '/features', '/contact', '/blog', '/faq', '/portfolio', '/pricing', '/privacy-policy', '/terms-of-service'];
    setIsPublicRoute(publicRoutes.includes(currentPath));
  };

  // Listen for route changes
  useEffect(() => {
    const handleRouteChange = () => {
      checkPublicRoute();
    };
    
    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('hashchange', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('hashchange', handleRouteChange);
    };
  }, []);

  useEffect(() => {
    checkPublicRoute();
  }, []);

  const handleLogin = async (email: string, password: string): Promise<AuthLoginResponse> => {
    try {
      showLoader('Authenticating User', true, 10);
      updateMessage('Validating Credentials');
      updateProgress(20);
      
      const loginData = await loginMutation({ email, password }).unwrap();
      
      updateMessage('Processing Login Response');
      updateProgress(50);
      
      if (loginData?.access_token) {
        updateMessage('Storing Session Data');
        updateProgress(70);
        
        localStorage.setItem('token', loginData.access_token);
        const transformedUser = transformUser(loginData.user);
        setUser(transformedUser);
        localStorage.setItem('user', JSON.stringify(transformedUser));
        
        updateMessage('Redirecting to Dashboard');
        updateProgress(90);
        
        // Role-based redirect
        if (transformedUser.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (transformedUser.role === 'manager') {
          navigate('/manager/dashboard');
        } else if (transformedUser.role === 'worker') {
          navigate('/worker/dashboard');
        } else if (transformedUser.role === 'customer') {
          navigate('/customer-portal');
        } else {
          navigate('/customer-portal');
        }
        
        updateProgress(100);
        setTimeout(() => hideLoader(), 500);
        
        return loginData;
      } else {
        hideLoader();
        throw new Error('Login failed: Invalid response from server');
      }
    } catch (error: any) {
      hideLoader();
      const message =
        error?.data?.error ||
        error?.data?.message ||
        (error?.status === 401 ? 'Invalid email or password' : error?.message) ||
        'Login failed. Please try again.';
      console.error('Login failed:', error);
      throw new Error(message);
    }
  };

  const handleRegister = async (userData: any) => {
    try {
      showLoader('Creating Account', true, 10);
      updateMessage('Validating Registration Data');
      updateProgress(30);
      
      const { data } = await registerMutation(userData);
      
      updateMessage('Setting Up User Profile');
      updateProgress(60);
      
      if (data?.access_token) {
        updateMessage('Completing Registration');
        updateProgress(90);
        
        const transformedUser = transformUser(data.user);
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(transformedUser));
        setUser(transformedUser);
        
        updateProgress(100);
        setTimeout(() => {
          hideLoader();
          navigate('/dashboard');
        }, 500);
      }
    } catch (error) {
      hideLoader();
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/auth/login');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    isPublicRoute,
    login: handleLogin,
    logout: logout,
    register: handleRegister,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return {
    ...context,
    login: context.login,
    register: context.register
  };
};
